import {
	computed,
	type MaybeRefOrGetter,
	nextTick,
	onBeforeUnmount,
	ref,
	toValue,
	watch,
} from 'vue';

import type { FollowingEvent } from '@/components/following/helpers/sectionHelper';

import {
	type ActivityFollowingMappingOptions,
	activityResponseToFollowingEvents,
} from '@/components/following/helpers/activityToFollowingMapper';
import followingService from '@/components/following/services/followingService';

const ABORT_ERROR_CODE = 'ERR_CANCELED';
const DEFAULT_PAGE_LIMIT = 20;
const SENTINEL_ROOT_MARGIN = '120px';

const isAbortError = (error: unknown): boolean => {
	return (error as { code?: string })?.code === ABORT_ERROR_CODE;
};

export interface UseWorkerActivityFeedOptions {
	workerId: MaybeRefOrGetter<string | null | undefined>;
	getMappingOptions?: () => ActivityFollowingMappingOptions | undefined;
	scrollRoot?: MaybeRefOrGetter<HTMLElement | null | undefined>;
}

export const useWorkerActivityFeed = (
	options: UseWorkerActivityFeedOptions
) => {
	const events = ref<FollowingEvent[] | null>(null);
	const isInitialLoading = ref(false);
	const isLoadingMore = ref(false);
	const nextCursor = ref<string | null>(null);
	const sentinelEl = ref<HTMLElement | null>(null);

	const hasNextPage = computed(() => Boolean(nextCursor.value));

	let abortController: AbortController | null = null;
	let intersectionObserver: IntersectionObserver | null = null;

	const resolveWorkerId = (): string | null =>
		toValue(options.workerId)?.trim() || null;

	const disconnectSentinelObserver = () => {
		intersectionObserver?.disconnect();
		intersectionObserver = null;
	};

	const loadInitial = async (): Promise<void> => {
		abortController?.abort();
		abortController = new AbortController();
		disconnectSentinelObserver();

		const workerId = resolveWorkerId();
		if (!workerId) {
			events.value = null;
			nextCursor.value = null;
			isInitialLoading.value = false;
			isLoadingMore.value = false;
			return;
		}

		isInitialLoading.value = true;
		isLoadingMore.value = false;
		events.value = null;
		nextCursor.value = null;

		try {
			const response = await followingService.fetchWorkerActivity({
				workerId,
				limit: DEFAULT_PAGE_LIMIT,
				signal: abortController.signal,
			});
			if (resolveWorkerId() !== workerId) return;

			events.value = activityResponseToFollowingEvents(
				response,
				options.getMappingOptions?.()
			);
			nextCursor.value = response.nextCursor;
		} catch (error: unknown) {
			if (isAbortError(error) || resolveWorkerId() !== workerId) return;
			events.value = [];
			nextCursor.value = null;
		} finally {
			if (resolveWorkerId() === workerId) {
				isInitialLoading.value = false;
			}
		}
	};

	const loadMore = async (): Promise<void> => {
		const workerId = resolveWorkerId();
		const cursor = nextCursor.value;
		if (!workerId || !cursor || isInitialLoading.value || isLoadingMore.value)
			return;

		isLoadingMore.value = true;
		try {
			const response = await followingService.fetchWorkerActivity({
				workerId,
				cursor,
				limit: DEFAULT_PAGE_LIMIT,
				signal: abortController?.signal,
			});
			if (resolveWorkerId() !== workerId) return;

			const mapped = activityResponseToFollowingEvents(
				response,
				options.getMappingOptions?.()
			);
			events.value = [...(events.value ?? []), ...mapped];
			nextCursor.value =
				response.nextCursor === cursor ? null : response.nextCursor;
		} catch (error: unknown) {
			if (isAbortError(error) || resolveWorkerId() !== workerId) return;
		} finally {
			if (resolveWorkerId() === workerId) {
				isLoadingMore.value = false;
			}
		}
	};

	const updateEventContent = (eventId: string, content: string): void => {
		if (!events.value) return;
		events.value = events.value.map((event) =>
			event.id === eventId
				? { ...event, line: { ...event.line, content } }
				: event
		);
	};

	const setupSentinelObserver = async () => {
		disconnectSentinelObserver();
		if (isInitialLoading.value || !hasNextPage.value) return;

		await nextTick();
		const el = sentinelEl.value;
		if (!el) return;

		const root = options.scrollRoot
			? (toValue(options.scrollRoot) ?? null)
			: null;
		intersectionObserver = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				void loadMore();
			},
			{ root, rootMargin: SENTINEL_ROOT_MARGIN, threshold: 0 }
		);
		intersectionObserver.observe(el);
	};

	watch(
		() => resolveWorkerId(),
		() => {
			void loadInitial();
		},
		{ immediate: true }
	);

	watch(
		[
			nextCursor,
			isInitialLoading,
			sentinelEl,
			() => (options.scrollRoot ? toValue(options.scrollRoot) : null),
		],
		() => {
			void setupSentinelObserver();
		}
	);

	onBeforeUnmount(() => {
		disconnectSentinelObserver();
		abortController?.abort();
		abortController = null;
	});

	return {
		events,
		isInitialLoading,
		isLoadingMore,
		hasNextPage,
		sentinelEl,
		reload: loadInitial,
		updateEventContent,
		loadMore,
	};
};
